import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import {
  accessControlApi,
  F002,
  F004,
  PermissionItem,
} from '../../../services/accessControlApi';

export function useAccessControlForm() {
  // Reference data
  const [levels, setLevels] = useState<F002[]>([]);
  const [subsystems, setSubsystems] = useState<F004[]>([]);

  // Current selections
  const [levelCode, setLevelCode] = useState('');
  const [levelName, setLevelName] = useState('');
  const [selectedSubsys, setSelectedSubsys] = useState('');

  // Permission grid
  const [items, setItems] = useState<PermissionItem[]>([]);

  // Copy fields
  const [copySource, setCopySource] = useState('');
  const [copyTarget, setCopyTarget] = useState('');

  // State
  const [loading, setLoading] = useState(false);
  const [levelValid, setLevelValid] = useState(false);

  // Load reference data on mount
  const loadRefData = useCallback(async () => {
    try {
      const [levelsRes, subsRes] = await Promise.all([
        accessControlApi.listLevels(),
        accessControlApi.listSubsystems(),
      ]);
      setLevels(levelsRes.data || []);
      setSubsystems(subsRes.data || []);
    } catch {
      message.error('載入基礎資料失敗');
    }
  }, []);

  useEffect(() => {
    loadRefData();
  }, [loadRefData]);

  // Validate level code (BR-02). Returns true if level is valid.
  const validateLevel = useCallback(async (code: string): Promise<boolean> => {
    if (!code.trim()) {
      setLevelValid(false);
      setLevelName('');
      setItems([]);
      return false;
    }
    try {
      const level = await accessControlApi.getLevel(code.trim());
      setLevelName(level.f002_11);
      setLevelValid(true);
      setCopyTarget(code.trim());
      return true;
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        setLevelName(vErrs[0].message);
      } else {
        setLevelName('*錯誤*無此等級!');
      }
      setLevelValid(false);
      setItems([]);
      return false;
    }
  }, []);

  // Load permissions for level + subsystem (BR-04)
  const loadPermissions = useCallback(async (lvl: string, sub: string) => {
    if (!lvl.trim() || !sub.trim()) return;
    setLoading(true);
    try {
      const resp = await accessControlApi.getPermissions(lvl.trim(), sub.trim());
      setItems(resp.items || []);
    } catch {
      message.error('載入使用權清單失敗');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle subsystem selection (BR-05)
  const handleSubsysSelect = useCallback((subsysCode: string) => {
    setSelectedSubsys(subsysCode);
    if (levelValid && levelCode.trim()) {
      loadPermissions(levelCode.trim(), subsysCode);
    }
  }, [levelValid, levelCode, loadPermissions]);

  // Handle level code change
  const handleLevelChange = useCallback((code: string) => {
    setLevelCode(code);
    if (code.trim().length === 0) {
      setLevelValid(false);
      setLevelName('');
      setItems([]);
    }
  }, []);

  // Validate and load permissions for a given code (called from LookupInput onCodeConfirm)
  const validateAndLoad = useCallback(async (code: string) => {
    const trimmed = code.trim();
    if (trimmed) {
      setLevelCode(trimmed);
      const valid = await validateLevel(trimmed);
      if (valid && selectedSubsys) {
        loadPermissions(trimmed, selectedSubsys);
      }
    }
  }, [selectedSubsys, validateLevel, loadPermissions]);

  // Handle level code blur (validate) — uses levelCode from state
  const handleLevelBlur = useCallback(async () => {
    if (levelCode.trim()) {
      const valid = await validateLevel(levelCode.trim());
      if (valid && selectedSubsys) {
        loadPermissions(levelCode.trim(), selectedSubsys);
      }
    }
  }, [levelCode, selectedSubsys, validateLevel, loadPermissions]);

  // Toggle single permission (BR-06 grid edit)
  const togglePermission = useCallback((index: number) => {
    setItems(prev => prev.map((item, i) =>
      i === index ? { ...item, f007_15: !item.f007_15 } : item
    ));
  }, []);

  // Set all permissions (BR-08/BR-09)
  const setAllPermissions = useCallback((value: boolean) => {
    setItems(prev => prev.map(item => ({ ...item, f007_15: value })));
  }, []);

  // Save permissions (BR-07)
  const save = useCallback(async () => {
    if (!levelCode.trim() || !selectedSubsys.trim()) {
      message.error('請先選擇使用等級和子系統');
      return;
    }
    setLoading(true);
    try {
      await accessControlApi.savePermissions({
        level_code: levelCode.trim(),
        subsys_code: selectedSubsys.trim(),
        items: items.map(item => ({
          f007_12: item.f007_12,
          f007_15: item.f007_15,
        })),
      });
      message.success('存檔完成!');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '存檔發生錯誤...');
      }
    } finally {
      setLoading(false);
    }
  }, [levelCode, selectedSubsys, items]);

  // Copy permissions (BR-14)
  const copyPermissions = useCallback(async () => {
    if (!copySource.trim() || !copyTarget.trim()) {
      message.error('請輸入來源和目的等級代碼');
      return;
    }
    setLoading(true);
    try {
      await accessControlApi.copyPermissions({
        source_level: copySource.trim(),
        target_level: copyTarget.trim(),
      });
      message.success('複製完成!');
      // Reload permissions if current level matches target
      if (levelCode.trim() === copyTarget.trim() && selectedSubsys) {
        await loadPermissions(levelCode.trim(), selectedSubsys);
      }
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '複製發生錯誤...');
      }
    } finally {
      setLoading(false);
    }
  }, [copySource, copyTarget, levelCode, selectedSubsys, loadPermissions]);

  return {
    // Reference data
    levels, subsystems,
    // Current state
    levelCode, levelName, levelValid,
    selectedSubsys, items, loading,
    // Copy fields
    copySource, copyTarget,
    setCopySource, setCopyTarget,
    // Actions
    handleLevelChange, handleLevelBlur, validateAndLoad,
    handleSubsysSelect,
    togglePermission, setAllPermissions,
    save, copyPermissions,
  };
}
